import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Typography,
  Row,
  Col,
  Spin,
  Alert,
  Tag,
  Progress,
  Table,
  Space,
  Button,
  Input,
  ProjectOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  theme
} from '@/shared/antd-imports';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useParams, useNavigate } from 'react-router-dom';
import clientPortalAPI from '@/services/api';
import TaskDrawer from '@/components/TaskDrawer/TaskDrawer';
import { PaymentScheduleCard } from '@/components/payment-schedule/PaymentScheduleCard';
import { BudgetExtensionsCard } from '@/components/budget-extensions/BudgetExtensionsCard';
import { MilestonesCard } from '@/components/milestone-ratings/MilestonesCard';
import { useTranslation } from 'react-i18next';
import { CheckSquareOutlined } from '@ant-design/icons';
import { ProjectDocumentsCard } from '@/components/documents/ProjectDocumentsCard';
import { MobileCardList } from '@/components/common/MobileCardList';
import { useIsPhone } from '@/hooks/useIsPhone';
// Spec 28 §7.5, §8 (stream abill): updates, agent progress and the AI-assisted label
import { ProjectUpdatesCard } from '@/components/updates/ProjectUpdatesCard';
import { AgentProgressChip, DisclosureTag } from '@/components/updates/AgentProgressChip';
import type { PortalDisclosure, TaskAgentFields } from '@/types/updates';

const { Title, Text } = Typography;

interface ProjectDetails {
  id: string;
  name: string;
  description: string | null;
  status: string;
  statusColor: string;
  startDate: string | null;
  endDate: string | null;
  createdAt: string;
  updatedAt: string;
  statistics: {
    totalTasks: number;
    completedTasks: number;
    progressPercentage: number;
  };
}

interface ProjectTask extends TaskAgentFields {
  id: string;
  name: string;
  description: string | null;
  status: string;
  statusColor: string;
  startDate: string | null;
  endDate: string | null;
  createdAt: string;
  updatedAt: string;
  unseenCommentsCount: number;
  /** Null for non-billable tasks: their logged time stays internal (ANS-56). */
  timeLogged: string | null;
}

const ProjectDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state) => state.ui.theme);
  // ANS-57: task cards instead of the table on phones
  const isPhone = useIsPhone();
  const [projectDetails, setProjectDetails] = useState<ProjectDetails | null>(null);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTasksLoading, setIsTasksLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tasksPagination, setTasksPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [disclosure, setDisclosure] = useState<PortalDisclosure | null>(null);
  const [isTaskDrawerOpen, setIsTaskDrawerOpen] = useState(false);

  const fetchProjectDetails = useCallback(async (projectId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await clientPortalAPI.getProjectDetails(projectId);

      if (response.done) {
        setProjectDetails(response.body as ProjectDetails);
      } else {
        setError(t('projects.details.loadError', { defaultValue: 'Failed to load project details. Please try again later.' }));
      }
    } catch (err) {
      setError(t('projects.details.loadError', { defaultValue: 'Failed to load project details. Please try again later.' }));
      console.error('Project details API error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const fetchProjectTasks = useCallback(async (projectId: string, page = 1, limit = 10, search = '') => {
    try {
      setIsTasksLoading(true);
      
      const response = await clientPortalAPI.getProjectTasks(projectId, { page, limit, search });

      if (response.done && response.body) {
        const data = response.body as { tasks: ProjectTask[]; total: number; page: number; limit: number; disclosure?: PortalDisclosure };
        setTasks(data.tasks);
        setDisclosure(data.disclosure ?? null);
        setTasksPagination({
          current: data.page,
          pageSize: data.limit,
          total: data.total
        });
      }
    } catch (err) {
      console.error('Project tasks API error:', err);
    } finally {
      setIsTasksLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchProjectDetails(id);
      fetchProjectTasks(id, 1, 10, '');
    }
  }, [id, fetchProjectDetails, fetchProjectTasks]);

  const handleTableChange = (pagination: { current?: number; pageSize?: number }) => {
    if (id) {
      fetchProjectTasks(id, pagination.current || 1, pagination.pageSize || 10, searchText);
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    if (id) {
      fetchProjectTasks(id, 1, tasksPagination.pageSize, value);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>{t('projects.details.loading', { defaultValue: 'Loading project details...' })}</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message={t('common.error', { defaultValue: 'Error' })}
        description={error}
        type="error"
        showIcon
        action={
          <Space>
            <Button onClick={() => navigate('/projects')}>
              {t('projects.details.back', { defaultValue: 'Back to Projects' })}
            </Button>
            <Button onClick={() => id && fetchProjectDetails(id)}>
              {t('projects.tryAgain', { defaultValue: 'Try Again' })}
            </Button>
          </Space>
        }
      />
    );
  }

  if (!projectDetails) {
    return (
      <Alert
        message={t('projects.details.notFound', { defaultValue: 'Project not found' })}
        description={t('projects.details.notFoundDescription', { defaultValue: 'The requested project could not be found.' })}
        type="warning"
        showIcon
        action={
          <Button onClick={() => navigate('/projects')}>
            {t('projects.details.back', { defaultValue: 'Back to Projects' })}
          </Button>
        }
      />
    );
  }

  const openTask = (taskId: string) => {
    setSelectedTaskId(taskId);
    setIsTaskDrawerOpen(true);
  };

  const plainDescription = (value: string | null): string | null => {
    if (!value) return null;
    const text = value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (!text) return null;
    return text.length > 80 ? `${text.substring(0, 80)}...` : text;
  };

  const taskColumns = [
    {
      title: t('projects.details.taskName', { defaultValue: 'Task Name' }),
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: ProjectTask) => (
        <div>
          <Typography.Link strong onClick={() => openTask(record.id)}>{text}</Typography.Link>
          {plainDescription(record.description) && (
            <div style={{ color: token.colorTextSecondary, fontSize: '12px', marginTop: 4 }}>
              {plainDescription(record.description)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('tasks.status', { defaultValue: 'Status' }),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string, record: ProjectTask) => (
        <Space size={4} wrap>
          <Tag color={record.statusColor} style={{ marginInlineEnd: 0 }}>
            {status || t('projects.details.noStatus', { defaultValue: 'No status' })}
          </Tag>
          <AgentProgressChip state={record.agent_progress} />
          {record.ai_assisted && <DisclosureTag disclosure={disclosure} />}
        </Space>
      ),
    },
    {
      title: t('tasks.startDate', { defaultValue: 'Start Date' }),
      dataIndex: 'startDate',
      key: 'startDate',
      width: 120,
      render: (date: string | null) => formatDate(date),
    },
    {
      title: t('tasks.endDate', { defaultValue: 'End Date' }),
      dataIndex: 'endDate',
      key: 'endDate',
      width: 120,
      render: (date: string | null) => formatDate(date),
    },
    {
      title: t('projects.details.timeLogged', { defaultValue: 'Time Logged' }),
      dataIndex: 'timeLogged',
      key: 'timeLogged',
      width: 130,
      render: (time: string | null) =>
        time === null ? (
          <Text type="secondary">-</Text>
        ) : (
          <Space size="small">
            <ClockCircleOutlined style={{ color: token.colorPrimary }} aria-hidden />
            <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{time || '0h 0m'}</Text>
          </Space>
        ),
    },
    {
      title: t('tasks.lastUpdated', { defaultValue: 'Last Updated' }),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (date: string) => (
        <Space size="small">
          <ClockCircleOutlined style={{ color: token.colorTextTertiary }} aria-hidden />
          <Text type="secondary">{formatDateTime(date)}</Text>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Button 
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/projects')}
        >
          {t('projects.details.back', { defaultValue: 'Back to Projects' })}
        </Button>
        {/* R2-G: what the provider needs from the client for this project */}
        <Button type="primary" icon={<CheckSquareOutlined />} onClick={() => navigate(`/projects/${projectDetails.id}/inputs`)}>
          {t('inputs.title', { defaultValue: 'What we need from you' })}
        </Button>
      </Space>

      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Space size={12} align="center">
                <ProjectOutlined style={{ fontSize: 24, color: token.colorPrimary }} />
                <div>
                  <Space size={8} align="center" wrap>
                    <Title level={4} style={{ margin: 0 }}>{projectDetails.name}</Title>
                    <Tag color={projectDetails.statusColor}>{projectDetails.status}</Tag>
                  </Space>
                  {(projectDetails.startDate || projectDetails.endDate) && (
                    <div style={{ marginTop: 6 }}>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        <CalendarOutlined style={{ marginRight: 4 }} aria-hidden />
                        {formatDate(projectDetails.startDate)} - {formatDate(projectDetails.endDate)}
                      </Text>
                    </div>
                  )}
                </div>
              </Space>
            </Col>
            <Col>
              <div style={{ textAlign: 'center', minWidth: 100 }}>
                <Progress
                  type="circle"
                  percent={projectDetails.statistics.progressPercentage}
                  size={64}
                  strokeWidth={8}
                  strokeColor={
                    projectDetails.statistics.progressPercentage === 100
                      ? token.colorSuccess
                      : token.colorPrimary
                  }
                  trailColor={
                    currentTheme === 'dark' ? token.colorBorderSecondary : token.colorFillSecondary
                  }
                  format={(percent) => (
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{percent ?? 0}%</span>
                  )}
                  status={projectDetails.statistics.progressPercentage === 100 ? 'success' : 'active'}
                />
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('projects.details.tasksDone', {
                      defaultValue: '{{done}}/{{total}} shared tasks',
                      done: projectDetails.statistics.completedTasks,
                      total: projectDetails.statistics.totalTasks,
                    })}
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
          {projectDetails.description && (
            <Row>
              <Col span={24}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {projectDetails.description}
                </Text>
              </Col>
            </Row>
          )}
        </Space>
      </Card>

      {/* R2-F (ANS-109): renders nothing unless the provider shares the payment schedule */}
      <PaymentScheduleCard projectId={projectDetails.id} />

      {/* Spec 16 §1: budget changes to approve; renders nothing when there are none */}
      <BudgetExtensionsCard projectId={projectDetails.id} />

      {/* Spec 15: shared milestones; the client rates each delivered one (renders nothing when none are shared) */}
      <MilestonesCard projectId={projectDetails.id} />

      {/* DOC-5 / DOC-3 portal half (ANS-56): documents the provider shared; approve when asked */}
      <ProjectDocumentsCard projectId={projectDetails.id} />

      {/* Spec 28 §7.5 (stream abill): progress updates the provider published on this project */}
      <ProjectUpdatesCard projectId={projectDetails.id} />

      <Card 
        title={
          <Space>
            <CheckCircleOutlined aria-hidden />
            {t('projects.details.tasks', { defaultValue: 'Tasks' })}
            <Tag>{tasksPagination.total}</Tag>
          </Space>
        }
        extra={
          !isPhone && (
            <Input.Search
              placeholder={t('projects.details.searchTasks', { defaultValue: 'Search tasks...' })}
              aria-label={t('projects.details.searchTasks', { defaultValue: 'Search tasks...' })}
              allowClear
              onSearch={handleSearch}
              style={{ width: 200 }}
              prefix={<SearchOutlined />}
            />
          )
        }
      >
        {isPhone && (
          <Input.Search
            placeholder={t('projects.details.searchTasks', { defaultValue: 'Search tasks...' })}
            aria-label={t('projects.details.searchTasks', { defaultValue: 'Search tasks...' })}
            allowClear
            onSearch={handleSearch}
            style={{ width: '100%', marginBottom: 12 }}
          />
        )}
        {isPhone ? (
          <MobileCardList<ProjectTask>
            items={tasks}
            rowKey={(task) => task.id}
            loading={isTasksLoading}
            ariaLabel={t('projects.details.tasks', { defaultValue: 'Tasks' })}
            emptyText={t('projects.details.noTasks', { defaultValue: 'No shared tasks yet. Tasks your provider shares with you appear here.' })}
            onOpen={(task) => openTask(task.id)}
            getAriaLabel={(task) => t('projects.details.openTask', { defaultValue: 'Open task {{name}}', name: task.name })}
            renderTitle={(task) => task.name}
            renderExtra={(task) => (
              <Space size={4} wrap>
                <Tag color={task.statusColor} style={{ marginInlineEnd: 0 }}>
                  {task.status || t('projects.details.noStatus', { defaultValue: 'No status' })}
                </Tag>
                <AgentProgressChip state={task.agent_progress} />
                {task.ai_assisted && <DisclosureTag disclosure={disclosure} />}
              </Space>
            )}
            renderMeta={(task) => (
              <>
                {plainDescription(task.description) && <div>{plainDescription(task.description)}</div>}
                <div>
                  {formatDate(task.startDate)} - {formatDate(task.endDate)}
                  {task.timeLogged ? ` · ${task.timeLogged}` : ''}
                </div>
                {task.unseenCommentsCount > 0 && (
                  <div>
                    {t('projects.details.newComments', {
                      defaultValue: '{{count}} new comment(s)',
                      count: task.unseenCommentsCount,
                    })}
                  </div>
                )}
              </>
            )}
            pagination={{
              current: tasksPagination.current,
              pageSize: tasksPagination.pageSize,
              total: tasksPagination.total,
              onChange: (page: number, pageSize: number) => id && fetchProjectTasks(id, page, pageSize, searchText),
            }}
          />
        ) : (
        <Table
          columns={taskColumns}
          dataSource={tasks}
          rowKey="id"
          loading={isTasksLoading}
          pagination={{
            current: tasksPagination.current,
            pageSize: tasksPagination.pageSize,
            total: tasksPagination.total,
            showSizeChanger: true,
            showTotal: (total, range) =>
              t('projects.details.showingTasks', {
                defaultValue: '{{start}}-{{end}} of {{total}} tasks',
                start: range[0],
                end: range[1],
                total,
              }),
            pageSizeOptions: ['10', '20', '50']
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          locale={{
            emptyText: t('projects.details.noTasks', { defaultValue: 'No shared tasks yet. Tasks your provider shares with you appear here.' })
          }}
          size="small"
          onRow={(record) => ({
            onKeyDown: (event) => {
              if (event.key === 'Enter' && event.target === event.currentTarget) openTask(record.id);
            },
            tabIndex: 0,
          })}
        />
        )}
      </Card>

      <TaskDrawer
        open={isTaskDrawerOpen}
        taskId={selectedTaskId}
        unseenCommentsCount={
          selectedTaskId
            ? tasks.find(task => task.id === selectedTaskId)?.unseenCommentsCount || 0
            : 0
        }
        onClose={() => {
          setIsTaskDrawerOpen(false);
          setSelectedTaskId(null);
        }}
        onTaskUpdated={() => {
          if (id) {
            fetchProjectTasks(id, tasksPagination.current, tasksPagination.pageSize, searchText);
          }
        }}
      />
    </div>
  );
};

export default ProjectDetailsPage;